import React, { useState } from 'react';
import { X, ShieldCheck } from 'lucide-react';
import { api, setSessionToken } from '../../services/api';
import { User } from '../../types';
import styles from './AuthModal.module.css';

interface TwoFactorModalProps {
  isOpen: boolean;
  userId: string;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

export const TwoFactorModal: React.FC<TwoFactorModalProps> = ({
  isOpen,
  userId,
  onClose,
  onSuccess,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.verify2FA(userId, code);
      setSessionToken(res.token, res.csrfToken);
      localStorage.setItem('cordle_user', JSON.stringify(res.user));
      onSuccess(res.user);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Invalid 2FA authentication code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          <X size={20} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'rgba(99, 102, 241, 0.15)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--accent-primary)',
            }}
          >
            <ShieldCheck size={28} />
          </div>
        </div>

        <h2 className={styles.title} style={{ textAlign: 'center' }}>
          Two-Factor Verification
        </h2>
        <p className={styles.subtitle} style={{ textAlign: 'center' }}>
          Enter the 6-digit TOTP security code from your Authenticator app.
        </p>

        {error && <div className={styles.errorBox}>{error}</div>}

        <form onSubmit={handleVerify}>
          <div className={styles.formGroup}>
            <input
              type="text"
              className={styles.input}
              placeholder="123456"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.3em', fontWeight: '700' }}
              required
            />
          </div>

          <button type="submit" className={styles.submitBtn} disabled={loading || code.length !== 6}>
            {loading ? 'Verifying...' : 'Authenticate'}
          </button>
        </form>
      </div>
    </div>
  );
};

